// The GATE processor for the channel tuning screen.
//
// A GATE threshold in dB is directly comparable with the PRE GATE meter's dBFS
// (measured: CH1's -54 dBFS noise floor sat below a -52 dB threshold and the gate
// stayed shut), which is what earns the lane rack's one gesture — the threshold is a
// fader cap dragged on the input meter itself.

import { formatDyn } from "../core/control/translate";
import { GATE_RANGE_OFF_DB } from "../core/control/vd";
import { GR_FLOOR_DB } from "../core/meters";
import { controlId, GATE_SCOPE } from "../core/midi/controls";
import type { ControlParam } from "../core/midi/controls";
import { bindChannelStrip, pairOwnNodes, subObjectIo } from "./dyn-chan";
import { transferPlot } from "./dyn-plot";
import { flagOffNote, HI_DB } from "./dyn-screen";
import type { DynPlotProcessor } from "./dyn-screen";

/** Input axis: the exact domain a GATE threshold can occupy, so a cap position
 *  maps to a threshold value linearly. */
const LO_DB = -72;

// The curve's output axis, which is deliberately NOT the input axis. The closed
// shelf sits at threshold + range, and for most of the range domain that falls
// below the -72 dB the input spans — at the factory settings -50 + -56 = -106 dB.
// Sharing the input's floor pinned every range past -22 dB to the same line, so
// at the factory threshold 70% of the range domain drew an identical picture and
// range was invisible. Running the output axis to the GR floor puts the shelf on
// scale: moving range from -30 to -56 shifts it by 20% of the plot height, from
// 0% before. A log-compressed axis was measured too and is worse (8.5%) — dB is
// already a log unit, and compressing it again squeezes exactly the deep region
// range lives in.
const OUT_LO_DB = -128;
/** Output-axis gridlines. Coarser than the input's: the axis is 1.8× longer and
 *  the region below -72 dB is context, not something to read off. */
const OUT_TICKS = [0, -24, -48, -72, -96, -128];

const io = subObjectIo("gate");

export const GATE_DYN: DynPlotProcessor = {
  // A linked pair's screen reads its primary's Signal Type and both members' meter
  // addresses, so the pair is what this screen draws rather than the node it was opened on.
  ownNodes: pairOwnNodes,
  key: "gate",
  loDb: LO_DB,
  // The step every rack on every screen reads in. The FLOOR is still this processor's own
  // — a gate threshold reaches -72 where a compressor's stops at -54 — but the grid the
  // bars are read against is one grid, so moving between screens does not change what a
  // tick is worth. The ruler stops one step short of the floor, so a -72 floor labels
  // down to -66.
  tickStep: 6,
  title: (m) => m.dynTuning.gate.title,
  // A gate's reduction runs the whole ruler (range reaches -∞), so the
  // shared tick column reads for it — a GR bar down to the -60 tick is 60 dB.
  //
  // No offset either, and none is possible to want: a gate has no makeup, so its reduction
  // and the level it was taken off cannot run into each other on the shared ruler.
  bind: (ctx) =>
    bindChannelStrip(ctx, {
      fields: (dyn) => dyn.gate,
      grKind: "gate",
      inTapKey: "pregate",
      outTapKey: "precomp",
      cap: "threshold",
    }),
  // No Display bar: the rack is two columns since the reduction moved onto PRE COMP, so it
  // fits beside the curve and both are on screen at once — the EQ and DUCKER arrangement.
  ...transferPlot({
    loDb: LO_DB,
    outLoDb: OUT_LO_DB,
    outTicks: OUT_TICKS,
    hint: (m) => m.dynTuning.gate.curveHint,
  }),
  offNote: (ctx) => flagOffNote(ctx, "gateOn"),
  read: io.read,
  patch: io.patch,
  // Every GATE value is a slider on the catalog's `gate` scope, so the key maps
  // straight through; there is no enum row on this screen to exclude.
  controlId: (ctx, key) => controlId(ctx.nodeId, key as ControlParam, GATE_SCOPE),
  // The curve carries one editable value, so a press on it is unambiguous.
  plotDragsCap: true,

  drawCurve: (c, g, v, tok) => {
    const thr = v.get("threshold");
    const rangeDb = v.get("range");
    // range at its -∞ notch closes completely, and the axis floor is what stands for that
    // (it is the GR meters' own floor, and the label prints "-∞"): pinning it there is the
    // representation, not a clamp. A FINITE range deep enough to fall below the axis —
    // threshold -72…-56 with the deepest ranges reaches -144 — is a different thing, and
    // is drawn at its true depth so the host's clip takes it off the frame. Clamping it
    // drew the same picture as -∞ for a gate that is not closed.
    const closed = rangeDb <= GATE_RANGE_OFF_DB;
    const drop = closed ? GR_FLOOR_DB : rangeDb;
    const shelfY = (db: number): number => g.py(closed ? Math.max(db, OUT_LO_DB) : db);

    c.strokeStyle = tok["--led"];
    c.lineWidth = 2;
    c.beginPath();
    c.moveTo(g.px(LO_DB), shelfY(LO_DB + drop));
    c.lineTo(g.px(thr), shelfY(thr + drop));
    c.stroke();
    c.beginPath();
    c.moveTo(g.px(thr), g.py(thr));
    c.lineTo(g.px(HI_DB), g.py(HI_DB));
    c.stroke();

    // The knee's drop, labelled with the range it represents. Only a -∞ range
    // reaches the axis floor; every finite range lands on scale, which is the
    // point of running the output axis past the input's.
    c.strokeStyle = tok["--gr"];
    c.setLineDash([3, 3]);
    c.beginPath();
    c.moveTo(g.px(thr), g.py(thr));
    c.lineTo(g.px(thr), shelfY(thr + drop));
    c.stroke();
    c.setLineDash([]);
    c.fillStyle = tok["--gr"];
    const right = g.px(thr) > g.w - 94;
    c.textAlign = right ? "right" : "left";
    const shown = closed ? "-∞" : formatDyn(rangeDb, "dbWhole");
    // The label is clamped where the line is not: it names the range, so it has to stay
    // readable even when the shelf it belongs to has left the frame.
    c.fillText(
      shown,
      g.px(thr) + (right ? -6 : 6),
      Math.min(g.py(Math.max(thr + drop, OUT_LO_DB)) - 6, g.h - g.pad.b - 4),
    );

    c.strokeStyle = tok["--led"];
    c.globalAlpha = 0.35;
    c.lineWidth = 1;
    c.beginPath();
    c.moveTo(g.px(thr) + 0.5, g.pad.t);
    c.lineTo(g.px(thr) + 0.5, g.h - g.pad.b);
    c.stroke();
    c.globalAlpha = 1;
    c.fillStyle = tok["--led"];
    c.textAlign = "left";
    c.fillText(formatDyn(thr, "dbWhole"), Math.min(g.px(thr) + 5, g.w - 74), g.pad.t + 11);
  },
};
